import { ReadAudience } from '@tinct/core-api-models';
import { useDeleteAudience } from '@tinct/core-api';
import {
  Alert,
  AlertTitle,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@tinct/web-components';
import { TriangleAlertIcon } from 'lucide-react';
import { toast } from 'sonner';

type DeleteAudienceModalProps = {
  audience: ReadAudience;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDeleted?: () => void;
};

export const DeleteAudienceModal = ({
  audience,
  open,
  onOpenChange,
  onDeleted,
}: DeleteAudienceModalProps) => {
  const campaigns = audience.campaigns ?? [];
  const isUsed = campaigns.length > 0;

  const { mutate: deleteAudience, isPending } = useDeleteAudience({
    onSuccess: () => {
      toast.success('Audience deleted');
      onOpenChange(false);
      onDeleted?.();
    },
  });

  const handleConfirm = () => {
    if (!audience.id) return;
    deleteAudience({ audienceId: audience.id });
  };

  if (isUsed) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[425px] rounded-3xl p-8">
          <DialogHeader>
            <DialogTitle>Delete audience</DialogTitle>
          </DialogHeader>
          <Alert variant="destructive" className="rounded-2xl">
            <TriangleAlertIcon className="size-4" />
            <AlertTitle>
              Cannot delete this audience. Remove it from these campaigns first :
            </AlertTitle>
          </Alert>
          <div className="text-sm text-foreground">
            {campaigns.map((c) => (
              <p key={c.id}>• Campaign "{c.name}"</p>
            ))}
          </div>
          <DialogFooter className="sm:justify-end">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Ok
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px] rounded-3xl p-8">
        <DialogHeader>
          <DialogTitle>Delete audience</DialogTitle>
          <DialogDescription>
            <span className="font-semibold">{audience.name}</span>
            {' will be permanently deleted.'}
            <br />
            {'Companies will not be deleted.'}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="sm:justify-between">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={isPending}
          >
            Confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
